import React from 'react';
import queryString from 'query-string';
import { DebugInfo, Loading, SystemText } from 'e4shared';
import { useDispatch, useSelector } from 'react-redux';
import { setPrismMenu } from 'actions/ui';
import PageHeader from 'components/general/pageHeader';
import Toggle from 'components/prism/toggle';
import { apiGet } from 'services';
import Overview from './components/overview';
import Reviews from './components/reviews';
import Library from './components/library';
import Objectives from './components/objectives';
import Pathways from './components/pathways';
import PathwayDetail from './components/pathwayDetail';

export const TeamMember = ({ match, location }) => {
    const dispatch = useDispatch();
    const [profile, setProfile] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [pathwayId, setPathwayId] = React.useState(null);
    const [loadingStats, setLoadingStats] = React.useState(true);
    const [tab, setTab] = React.useState('overview');
    const [launchedReviewsCount, setLaunchedReviewsCount] = React.useState(0);
    const { user } = useSelector(state => state.user);
    const profileId = match.params.id;

    const queryValues = queryString.parse(location.search);

    React.useEffect(() => {
        if (queryValues?.pathwayId) {
            setTab('pathways');
            setPathwayId(queryValues.pathwayId);
        } else if (queryValues?.tab) {
            setTab(queryValues.tab);
        }
    }, [queryValues]);

    React.useEffect(() => {
        dispatch(setPrismMenu('Team'));
    }, []);

    React.useEffect(() => {
        DebugInfo('component', 'TeamMember');
    }, []);

    React.useEffect(() => {
        setLoading(true);
        if (profileId) {
            apiGet(`team-member/${profileId}`, user.guid, response => {
                setProfile(response);
                setLoading(false);
            });
        }
    }, [profileId]);

    // Load appraisals and calculate count of reviews with launch dates that have passed
    React.useEffect(() => {
        if (profileId && user.guid) {
            apiGet(`appraisals/${profileId}`, user.guid, response => {
                // Filter to only count scheduled reviews (no submission) whose launch date has passed
                const launchedScheduledReviews =
                    response?.filter(appraisal => {
                        // Only count scheduled reviews (not completed)
                        if (appraisal.submission) return false;
                        // If no launch date, include the appraisal
                        if (!appraisal.launch) return true;
                        // Only include if launch date is in the past or is today
                        const launchDate = new Date(appraisal.launch);
                        const now = new Date();
                        return launchDate <= now;
                    }) || [];
                setLaunchedReviewsCount(launchedScheduledReviews.length);
            });
        }
    }, [profileId, user.guid]);

    const reloadStats = () => {
        setLoadingStats(true);
        apiGet(`team-member/${profileId}`, user.guid, response => {
            setProfile(response);
            setLoadingStats(false);
        });
        // Also reload the launched reviews count
        if (profileId && user.guid) {
            apiGet(`appraisals/${profileId}`, user.guid, response => {
                const launchedScheduledReviews =
                    response?.filter(appraisal => {
                        if (appraisal.submission) return false;
                        if (!appraisal.launch) return true;
                        const launchDate = new Date(appraisal.launch);
                        const now = new Date();
                        return launchDate <= now;
                    }) || [];
                setLaunchedReviewsCount(launchedScheduledReviews.length);
            });
        }
    };

    const mainContent = () => {
        switch (tab) {
            case 'reviews':
                return <Reviews profile={profile} reloadStats={reloadStats} />;
            case 'pathways':
                if (pathwayId) {
                    return <PathwayDetail pathwayId={pathwayId} profile={profile} />;
                }

                return <Pathways profile={profile} setPathwayId={setPathwayId} />;
            case 'library':
                return <Library profile={profile} />;
            case 'objectives':
                return <Objectives profile={profile} />;
            default:
                return <Overview profile={profile} />;
        }
    };

    return (
        <div>
            <PageHeader prismProfile={profile} team />
            <section className="content mtm10" data-testid="team-member-content">
                <Toggle
                    value={tab}
                    onChange={x => {
                        setTab(x);
                        setPathwayId(null);
                    }}
                >
                    <button value="overview">{SystemText('TeamMember/Overview')}</button>
                    <button value="reviews">
                        <div className="flex flex-center">
                            <span>{SystemText('TeamMember/Reviews')}</span>
                            {launchedReviewsCount > 0 && (
                                <span className="label label-danger ml10 btn-cornered">{launchedReviewsCount}</span>
                            )}
                        </div>
                    </button>
                    <button value="pathways">
                        <div className="flex flex-center">
                            <span>{SystemText('TeamMember/Pathways')}</span>
                        </div>
                    </button>
                    <button value="library">{SystemText('TeamMember/Library')}</button>
                    <button value="objectives">
                        <div className="flex flex-center">
                            <span>{SystemText('TeamMember/Objectives')}</span>
                            {profile?.awaiting > 0 && (
                                <span className="label label-danger ml10 btn-cornered">{profile.awaiting}</span>
                            )}
                        </div>
                    </button>
                </Toggle>
                {loading ? <Loading /> : mainContent()}
            </section>
        </div>
    );
};

export default TeamMember;
